import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import Layout from '../components/Layout'
import { supabase } from '../config/supabase'

const ContactDetailsDialog = ({ open, contact, onClose }) => {
  if (!contact) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        },
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            {contact.first_name} {contact.last_name}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {contact.membership_status && (
                <Chip 
                  label={contact.membership_status} 
                  color={contact.membership_status === 'Active' ? 'success' : 'default'}
                  size="small"
                />
              )}
              {contact.age_group && (
                <Chip 
                  label={contact.age_group}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Grid>

          {contact.phone && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon color="action" />
                <Link 
                  href={`tel:${contact.phone}`}
                  sx={{ 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {contact.phone}
                </Link>
              </Box>
            </Grid>
          )}

          {contact.email && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="action" />
                <Link 
                  href={`mailto:${contact.email}`}
                  sx={{ 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {contact.email}
                </Link>
              </Box>
            </Grid>
          )}

          {contact.address && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon color="action" />
                <Typography>{contact.address}</Typography>
              </Box>
            </Grid>
          )}

          {(contact.gender || contact.marital_status) && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="action" />
                <Typography>
                  {[contact.gender, contact.marital_status].filter(Boolean).join(' • ')}
                </Typography>
              </Box>
            </Grid>
          )}

          {contact.hometown && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="action" />
                <Typography>{contact.hometown}</Typography>
              </Box>
            </Grid>
          )}

          {contact.date_of_birth && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="action" />
                <Typography>
                  Born: {new Date(contact.date_of_birth).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
          )}

          {contact.membership_date && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="action" />
                <Typography>
                  Member since: {new Date(contact.membership_date).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
          )}

          {contact.baptism_date && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="action" />
                <Typography>
                  Baptized: {new Date(contact.baptism_date).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
          )}

          {contact.family?.family_name && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon color="action" />
                <Typography>
                  Family: {contact.family.family_name}
                </Typography>
              </Box>
            </Grid>
          )}

          {contact.previous_membership && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon color="action" />
                <Typography>
                  Previous Church: {contact.previous_membership}
                </Typography>
              </Box>
            </Grid>
          )}

          {contact.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Notes
              </Typography>
              <Typography
                sx={{
                  whiteSpace: 'pre-wrap',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {contact.notes}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const MobileContactCard = ({ contact, onDelete, onView }) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      sx={{ 
        mb: 2,
        borderRadius: 2,
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" component="div">
            {contact.first_name} {contact.last_name}
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={() => onView(contact)}
              sx={{ mr: 1 }}
              color="primary"
            >
              <ViewIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => navigate(`/contacts/edit/${contact.id}`)}
              sx={{ mr: 1 }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(contact.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {contact.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="action" fontSize="small" />
              <Link 
                href={`tel:${contact.phone}`}
                sx={{ 
                  textDecoration: 'none',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {contact.phone}
              </Link>
            </Box>
          )}
          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="action" fontSize="small" />
              <Link 
                href={`mailto:${contact.email}`}
                sx={{ 
                  textDecoration: 'none',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {contact.email}
              </Link>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Chip 
              label={contact.membership_status} 
              size="small"
              color={contact.membership_status === 'Active' ? 'success' : 'default'}
            />
            <Chip 
              label={contact.age_group} 
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function ContactList() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Fetch all contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  // Handle search filtering
  useEffect(() => {
    if (!contacts) return;
    
    const filtered = contacts.filter(contact => {
      const searchLower = searchTerm.toLowerCase();
      return (
        contact.first_name?.toLowerCase().includes(searchLower) ||
        contact.last_name?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.phone?.toLowerCase().includes(searchLower) ||
        contact.address?.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredContacts(filtered);
  }, [searchTerm, contacts]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          address,
          gender,
          marital_status,
          date_of_birth,
          age_group,
          membership_status,
          membership_date,
          baptism_date,
          previous_membership,
          hometown,
          notes,
          family:families (
            id,
            family_name
          )
        `)
        .order('first_name');

      if (error) throw error;
      
      const contactsData = data || [];
      setContacts(contactsData);
      setFilteredContacts(contactsData);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedContact(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        const { error } = await supabase
          .from('contacts')
          .delete()
          .eq('id', id);

        if (error) throw error;

        // Update local state after successful deletion
        const updatedContacts = contacts.filter(contact => contact.id !== id);
        setContacts(updatedContacts);
        setFilteredContacts(updatedContacts);
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 4 
        }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Contacts
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flex: 1,
            maxWidth: { xs: '100%', md: '600px' }
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/contacts/new')}
              sx={{
                whiteSpace: 'nowrap',
                borderRadius: 2,
              }}
            >
              New Contact
            </Button>
          </Box>
        </Box>

        {isMobile ? (
          <Box>
            {loading ? (
              <Typography align="center" sx={{ py: 4 }}>Loading...</Typography>
            ) : filteredContacts.length === 0 ? (
              <Typography align="center" sx={{ py: 4 }}>No contacts found</Typography>
            ) : (
              filteredContacts.map((contact) => (
                <MobileContactCard
                  key={contact.id}
                  contact={contact}
                  onDelete={handleDelete}
                  onView={handleViewContact}
                />
              ))
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact Info</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Family</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredContacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      No contacts found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContacts.map((contact) => (
                    <TableRow 
                      key={contact.id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="subtitle2">
                          {contact.first_name} {contact.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {contact.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Link 
                                href={`tel:${contact.phone}`}
                                sx={{ 
                                  textDecoration: 'none',
                                  color: 'primary.main',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {contact.phone}
                              </Link>
                            </Box>
                          )}
                          {contact.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Link 
                                href={`mailto:${contact.email}`}
                                sx={{ 
                                  textDecoration: 'none',
                                  color: 'primary.main',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {contact.email}
                              </Link>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={contact.membership_status} 
                          size="small"
                          color={contact.membership_status === 'Active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {contact.family?.family_name}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewContact(contact)}
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/contacts/edit/${contact.id}`)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(contact.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <ContactDetailsDialog
          open={detailsOpen}
          contact={selectedContact}
          onClose={handleCloseDetails}
        />
      </Box>
    </Layout>
  );
}
